import * as React from 'react';
import {Query} from 'react-apollo';

import _ from 'underscore';

import {SelectNew} from 'shells/select-new/select-new';
import {SectionLabel} from 'shells/typography/section-label';

import type {
    GetCtctContacts as GetCtctContactsQuery,
    GetCtctContactsVariables as GetCtctContactsQueryVariables,
    CtCtContact as CtCtContactFragment,
} from 'nutshell-graphql-types';

import {AddToConstantContact} from './add-to-constant-contact';
import {ConstantContactDetails} from './constant-contact-details';
import GET_CTCT_CONTACTS from './graphql/queries/get-ctct-contacts.graphql';

import styles from './constant-contact-rollup.module.css';

class ConstantContactQuery extends Query<GetCtctContactsQuery, GetCtctContactsQueryVariables> {}

type Props = {
    entityId: string;
    entityName: string;
    model: any;
};

export function ConstantContactRollup(props: Props) {
    return (
        <React.Fragment>
            <SectionLabel>Constant Contact</SectionLabel>
            <ConstantContactQuery query={GET_CTCT_CONTACTS} variables={{entityId: props.entityId}}>
                {({data, loading, error, refetch}) => {
                    if ((!data || !data.coreEntity) && loading) {
                        return <div className={styles.loading}>Loading…</div>;
                    }

                    if (!data || !data.coreEntity || error) {
                        return (
                            <div>
                                We’re sorry, but we’re having trouble connecting to Constant
                                Contact.
                            </div>
                        );
                    }

                    const allContacts = data.coreEntity.ctctContacts;

                    return (
                        <ConstantContactRollupComponent
                            contacts={allContacts}
                            primaryContact={data.coreEntity.primaryCtctContact}
                            entityName={props.entityName}
                            entityId={props.entityId}
                            refetch={() => refetch()}
                            model={props.model}
                        />
                    );
                }}
            </ConstantContactQuery>
        </React.Fragment>
    );
}

/**
 * This is a separate component so that we can useEffect to select a contact when the list of
 * contacts changes, which might happen when the cache is updated, for instance.
 */
function ConstantContactRollupComponent(props: {
    contacts: CtCtContactFragment[];
    primaryContact: CtCtContactFragment | null | undefined;
    entityName: string;
    entityId: string;
    refetch: () => Promise<any>;
    model: any;
}) {
    const [selectedContactId, setSelectedContactId] = React.useState<string | null | undefined>(
        null
    );
    React.useEffect(() => {
        if (
            props.primaryContact &&
            props.contacts.length &&
            // First time loading, no selection yet
            (!selectedContactId ||
                // The selected contact was removed
                !props.contacts.find((contact) => contact.id === selectedContactId))
        ) {
            setSelectedContactId(props.primaryContact.id);
        }
    }, [props.contacts, props.primaryContact, selectedContactId]);
    React.useEffect(() => {
        props.model.listenTo(
            props.model,
            'add:emails update:emails remove:emails',
            _.debounce(
                () => {
                    props.refetch();
                },
                250,
                true
            )
        );

        return () => {
            props.model.stopListening(props.model, 'add:emails update:emails remove:emails');
        };
    }, [props]);

    const selectedContact = props.contacts.find((contact) => contact.id === selectedContactId);

    if (selectedContact) {
        return (
            <React.Fragment>
                {props.contacts.length > 1 ? (
                    <div className={styles['contact-select']}>
                        <SelectNew
                            selectValues={props.contacts}
                            value={selectedContact}
                            onChange={(newContact) => {
                                setSelectedContactId(newContact.id);
                            }}
                            labelKey='emailAddress'
                        />
                    </div>
                ) : null}
                <ConstantContactDetails
                    ctctContact={selectedContact}
                    entityName={props.entityName}
                    entityId={props.entityId}
                />
            </React.Fragment>
        );
    } else {
        // TODO: Check for unsubscribed status, show different UI
        return <AddToConstantContact entityId={props.entityId} />;
    }
}
